import { CircleSlash, FileSignature, FolderKanban, LetterText, Package, ShieldCheck, FileText, Image, HardHat } from "lucide-react";
import { z } from "zod";

export const LINE_ITEM_TYPES = [
  { label: "Labor", value: "Labor", icon: HardHat, color: "amber-600", description: "Labor item" },
  { label: "Product", value: "Product", icon: Package, color: "primary", description: "Physical items or materials" },
  // { label: "Service", value: "Service", icon: Wrench, color: "purple-500", description: "Text or service work" },
  { label: "Optional Work", value: "Summary", icon: CircleSlash, color: "orange-400", description: "Additional optional work items" },
  { label: "Signature Required", value: "Signature", icon: FileSignature, color: "red-500", description: "Signature requirement line" },
  // { label: "Separator", value: "Separator", icon: Minus, color: "slate-500", description: "Visual separator line" },
  { label: "Warranty", value: "Warranty", icon: ShieldCheck, color: "emerald-600", description: "Warranty information" },
  { label: "Text", value: "Text", icon: LetterText, color: "stone-500", description: "Additional text or notes" },
  { label: "File", value: "File", icon: FileText, color: "blue-500", description: "Uploaded file" },
  { label: "Photo", value: "Photo", icon: Image, color: "green-500", description: "Uploaded photo" },
] as const;

// These types are internal and won't be shown in the UI directly
export const INTERNAL_LINE_ITEM_TYPES = [
  ...LINE_ITEM_TYPES,
  { label: "Group Start", value: "GroupStart", icon: FolderKanban, color: "blue-500", description: "Start of a group of line items" },
  { label: "Group End", value: "GroupEnd", icon: FolderKanban, color: "blue-500", description: "End of a group of line items" },
] as const;

// This is what will be displayed in the UI for creating groups
export const UI_LINE_ITEM_TYPES = [
  ...LINE_ITEM_TYPES,
  { label: "Group", value: "Group", icon: FolderKanban, color: "blue-500", description: "Group of line items" },
] as const;

export const UNIT_OPTIONS = [
  { label: "Square Feet", value: "SF" },
  { label: "Linear Feet", value: "LF" },
  { label: "Each", value: "EA" },
  { label: "Hour", value: "HR" },
  { label: "Gallon", value: "GAL" },
  { label: "Pound", value: "LB" },
  { label: "Square", value: "SQ" },
  { label: "Bucket", value: "Bucket" },
  { label: "Bundle", value: "Bundle" },
  { label: "Carton", value: "Carton" },
  { label: "Roll", value: "Roll" },
  { label: "N/A", value: "NA" },
] as const;

export type LineItemType = typeof INTERNAL_LINE_ITEM_TYPES[number]["value"];
export type UnitType = typeof UNIT_OPTIONS[number]["value"];

export const ALLOWED_PRODUCT_TYPE_FOR_TEXT: LineItemType[] = ["Product", "Labor"];

export const LineItemSchema = z.object({
  id: z.number().optional(),
  trackingId: z.string(),
  type: z.enum(INTERNAL_LINE_ITEM_TYPES.map(t => t.value) as [string, ...string[]]),
  title: z.string().nullable(),
  description: z.string().nullable(),
  quantity: z.number().nullable(),
  unit: z.enum(UNIT_OPTIONS.map(u => u.value) as [string, ...string[]]).nullable(),
  unitCost: z.number().nullable(),
  cost: z.number().nullable(),
  markup: z.number().nullable(),
  applyTax: z.boolean(),
  excludeAdditionalMarkup: z.boolean().optional(),
  materialTypeId: z.number().nullable(),
  materialStyleId: z.number().nullable(),
  materialSupplierId: z.number().nullable(),
  laborCategoryId: z.string().nullable(),
  laborTypeId: z.string().nullable(),
  typeId: z.number().nullable(),
  useQuoteFile: z.boolean(),
  useClaimFile: z.boolean(),
  useWorkOrderFile: z.boolean(),
  date: z.string().nullable(),
  accepted: z.boolean(),
  initials: z.string().nullable(),
  reason: z.string().nullable(),
  reasonNotes: z.string().nullable(),
  order: z.number().nullable(),
  materialStyle: z.string().nullable(),
  // For GroupStart items only
  groupName: z.string().nullable(),
  // For frontend use only (not sent to backend)
  isCollapsed: z.boolean().optional(),
  // Section ID for quote sections
  sectionId: z.number().nullable().optional(),
  // For File items only
  filename: z.string().nullable().optional(),
  filenameS3: z.string().nullable().optional(),
  appliedFormulaId: z.number().nullable().optional(),
  // For Product items that are nested within Text items
  parentTextTrackingId: z.string().nullable().optional(),
});

export type LineItem = z.infer<typeof LineItemSchema>;

export const LineItemsSchema = z.array(LineItemSchema);
export type LineItems = z.infer<typeof LineItemsSchema>;

// Frontend hierarchical group model
export interface GroupedLineItem extends LineItem {
  groupStartId?: number;
  groupEndId?: number | undefined;
  children?: GroupedLineItem[];
  isGroup?: boolean;
  isText?: boolean;
}

// Quote Section Schema
export const QuoteSectionSchema = z.object({
  id: z.number().optional(),
  trackingId: z.string(),
  name: z.string(),
  order: z.number(),
  wotype: z.string().nullable(),
  // For frontend use only
  isCollapsed: z.boolean().optional(),
  crewId: z.number().nullable(),
});

export type QuoteSection = z.infer<typeof QuoteSectionSchema>;

export const QuoteSectionsSchema = z.array(QuoteSectionSchema);

export type QuoteSections = z.infer<typeof QuoteSectionsSchema>;